import { Empty } from "@bufbuild/protobuf";

import {
  AdminGetGroupsForUserRequest,
  AdminGetGroupsForUserResponse,
} from "@safetyculture/s12-apis-connect-web/s12/groups/admin/v1/admin_groups_pb";
import {
  GroupType,
  PaginationOption,
} from "@safetyculture/s12-apis-connect-web/s12/groups/v1/common_pb";
import {
  EnableMultiOrgByEmailRequest,
  EnableMultiOrgByEmailResponse,
  GetUserRequest as GetUserIdentityRequest,
  GetUserResponse as GetUserIdentityResponse,
  UpdateEmailRequest,
  UpdateEmailResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  UpdateUsernameRequest,
  UpdateUsernameResponse,
} from "@safetyculture/s12-apis-connect-web/s12/identity/admin/v1/identity_management_pb";
import {
  AnalyseDiagnosticFileRequest,
  AnalyseDiagnosticFileResponse,
  ConfirmUserRequest,
  ConfirmUserResponse,
  DeleteUserTokenRequest,
  DeleteUserTokenResponse,
  GetUserByEmailRequest,
  GetUserByEmailResponse,
  GetUserDiagnosticFilesRequest,
  GetUserDiagnosticFilesResponse,
  GetUserInspectionQuotaRequest,
  GetUserInspectionQuotaResponse,
  GetUserLoginStatusRequest,
  GetUserLoginStatusResponse,
  GetUserOrganisationsRequest,
  GetUserOrganisationsResponse,
  GetUserRequest,
  ListUserDevicesRequest,
  ListUserDevicesResponse,
  TriggerPasswordResetEmailRequest,
  TriggerPasswordResetEmailResponse,
  UnblockUserLoginRequest,
  UpdateUserAdminFlagRequest,
  UpdateUserAdminFlagResponse,
  UpdateUserInspectionQuotaRequest,
  UpdateUserInspectionQuotaResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

type GetUserParams = {
  userId: string;
};

export const getUser = async (
  { userId }: GetUserParams,
  headers: Record<RequestMetadata, string>
): Promise<User | undefined> => {
  const req = new GetUserRequest({
    userId,
  });

  return supportServiceConnectClient
    .getUser(req, { headers })
    .then((res) => res.user);
};

type GetUserIdentityParams = {
  userId: string;
};

export const getUserIdentity = async (
  { userId }: GetUserIdentityParams,
  headers: Record<RequestMetadata, string>
): Promise<GetUserIdentityResponse> => {
  const req = new GetUserIdentityRequest({
    userId,
  });

  return supportServiceConnectClient.getUserIdentity(req, { headers });
};

type GetUserLoginStatusParams = {
  authId: string;
};

export const getUserLoginStatus = async (
  { authId }: GetUserLoginStatusParams,
  headers: Record<RequestMetadata, string>
): Promise<GetUserLoginStatusResponse> => {
  const req = new GetUserLoginStatusRequest({
    authId,
  });

  return supportServiceConnectClient.getUserLoginStatus(req, { headers });
};

type UnblockUserLoginParams = {
  authId: string;
};

export const unblockUserLogin = async (
  { authId }: UnblockUserLoginParams,
  headers: Record<RequestMetadata, string>
): Promise<Empty> => {
  const req = new UnblockUserLoginRequest({
    authId,
  });

  return supportServiceConnectClient.unblockUserLogin(req, { headers });
};

type TriggerPasswordResetEmailParams = {
  email: string;
};

export const triggerPasswordResetEmail = async (
  { email }: TriggerPasswordResetEmailParams,
  headers: Record<RequestMetadata, string>
): Promise<TriggerPasswordResetEmailResponse> => {
  const req = new TriggerPasswordResetEmailRequest({
    email,
  });

  return supportServiceConnectClient.triggerPasswordResetEmail(req, {
    headers,
  });
};

type ConfirmUserParams = {
  userId: string;
};

export const confirmUser = async (
  { userId }: ConfirmUserParams,
  headers: Record<RequestMetadata, string>
): Promise<ConfirmUserResponse> => {
  const req = new ConfirmUserRequest({
    userId,
  });

  return supportServiceConnectClient.confirmUser(req, {
    headers,
  });
};

type UpdateEmailParams = {
  userId: string;
  email: string;
};

export const updateEmail = async (
  { userId, email }: UpdateEmailParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateEmailResponse> => {
  const req = new UpdateEmailRequest({
    userId,
    email: email.toLowerCase(),
  });

  return supportServiceConnectClient.updateEmail(req, {
    headers,
  });
};

type UpdateUsernameParams = {
  userId: string;
  username: string;
};

export const updateUsername = async (
  { userId, username }: UpdateUsernameParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateUsernameResponse> => {
  const req = new UpdateUsernameRequest({
    userId,
    username,
  });

  return supportServiceConnectClient.updateUsername(req, {
    headers,
  });
};

type UpdatePasswordParams = {
  userId: string;
  password: string;
};

export const updatePassword = async (
  { userId, password }: UpdatePasswordParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdatePasswordResponse> => {
  const req = new UpdatePasswordRequest({
    userId,
    password,
  });

  return supportServiceConnectClient.updatePassword(req, {
    headers,
  });
};

type GetUserInspectionQuotaParams = {
  userId: string;
  orgId: string;
};

export const getUserInspectionQuota = async (
  { userId, orgId }: GetUserInspectionQuotaParams,
  headers: Record<RequestMetadata, string>
): Promise<GetUserInspectionQuotaResponse> => {
  const req = new GetUserInspectionQuotaRequest({
    userId,
    orgId,
  });

  return supportServiceConnectClient.getUserInspectionQuota(req, { headers });
};

type UpdateUserInspectionQuotaParams = {
  userId: string;
  orgId: string;
  quota: number;
};

export const updateUserInspectionQuota = async (
  { userId, orgId, quota }: UpdateUserInspectionQuotaParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateUserInspectionQuotaResponse> => {
  const req = new UpdateUserInspectionQuotaRequest({
    userId,
    orgId,
    quota,
  });

  return supportServiceConnectClient.updateUserInspectionQuota(req, {
    headers,
  });
};

type GetUserDiagnosticFilesParams = {
  userId: string;
};

export const getUserDiagnosticFiles = async (
  { userId }: GetUserDiagnosticFilesParams,
  headers: Record<RequestMetadata, string>
): Promise<GetUserDiagnosticFilesResponse> => {
  const req = new GetUserDiagnosticFilesRequest({
    userId,
  });

  return supportServiceConnectClient.getUserDiagnosticFiles(req, { headers });
};

type AnalyseDiagnosticFileParams = {
  userId: string;
  filename: string;
  problemDescription: string;
  disableLogTrimming?: boolean;
};

export const analyseDiagnosticFile = async (
  {
    userId,
    filename,
    problemDescription,
    disableLogTrimming,
  }: AnalyseDiagnosticFileParams,
  headers: Record<RequestMetadata, string>
): Promise<AnalyseDiagnosticFileResponse> => {
  const req = new AnalyseDiagnosticFileRequest({
    userId,
    filename,
    problemDescription,
    disableExcessiveTrimOnLog: disableLogTrimming,
  });
  return supportServiceConnectClient.analyseDiagnosticFile(req, { headers });
};

type ListUserDevicesParams = {
  userId: string;
};

export const listUserDevices = async (
  { userId }: ListUserDevicesParams,
  headers: Record<RequestMetadata, string>
): Promise<ListUserDevicesResponse> => {
  const req = new ListUserDevicesRequest({
    userId,
  });

  return supportServiceConnectClient.listUserDevices(req, { headers });
};

type DeleteUserTokenParams = {
  userId: string;
  tokenId: string;
};

export const deleteUserToken = async (
  { userId, tokenId }: DeleteUserTokenParams,
  headers: Record<RequestMetadata, string>
): Promise<DeleteUserTokenResponse> => {
  const req = new DeleteUserTokenRequest({
    userId,
    tokenId,
  });

  return supportServiceConnectClient.deleteUserToken(req, { headers });
};

type EnableMultiOrgByEmailParams = {
  email: string;
};

export const enableMultiOrgByEmail = async (
  { email }: EnableMultiOrgByEmailParams,
  headers: Record<RequestMetadata, string>
): Promise<EnableMultiOrgByEmailResponse> => {
  const req = new EnableMultiOrgByEmailRequest({
    email,
  });

  return supportServiceConnectClient.enableMultiOrgByEmail(req, { headers });
};

type GetUserByEmailParams = {
  email: string;
};

export const getUserByEmail = async (
  { email }: GetUserByEmailParams,
  headers: Record<RequestMetadata, string>
): Promise<GetUserByEmailResponse> => {
  const req = new GetUserByEmailRequest({
    email,
  });

  return supportServiceConnectClient.getUserByEmail(req, { headers });
};

type GetUserOrgsParams = {
  userId: string;
};

export const getUserOrgs = async (
  { userId }: GetUserOrgsParams,
  headers: Record<RequestMetadata, string>
): Promise<GetUserOrganisationsResponse> => {
  const req = new GetUserOrganisationsRequest({
    userId,
  });

  return supportServiceConnectClient.getUserOrganisations(req, { headers });
};

type GetUserGroupsParams = {
  organizationId: string;
  userId: string;
  pageSize?: number;
  pageToken?: string;
  includeDeleted?: boolean;
  groupType?: GroupType;
  pagingOption?: PaginationOption;
};

export const getUserGroups = async (
  {
    organizationId,
    userId,
    pageToken,
    includeDeleted,
    groupType,
    pagingOption,
  }: GetUserGroupsParams,
  headers: Record<RequestMetadata, string>
): Promise<AdminGetGroupsForUserResponse> => {
  const req = new AdminGetGroupsForUserRequest({
    organisationId: organizationId,
    userId,
    pageToken,
    includeDeleted,
    groupType,
    pagingOption,
  });

  return supportServiceConnectClient.getUserGroups(req, { headers });
};

type UpdateUserAdminFlagParams = {
  userId: string;
  adminFlag: boolean;
};

export const updateUserAdminFlag = async (
  { userId, adminFlag }: UpdateUserAdminFlagParams,
  headers: Record<RequestMetadata, string>
): Promise<UpdateUserAdminFlagResponse> => {
  const req = new UpdateUserAdminFlagRequest({
    userId,
    adminFlag,
  });

  return supportServiceConnectClient.updateUserAdminFlag(req, { headers });
};
